import { Dialog, DialogWindow } from '../../../../widgets/dialog';
import { SearchInput } from '../../../../widgets/input';
import React, { FC, Suspense, useEffect, useState, useTransition } from 'react';
import { useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CatalogListItemModel } from 'src/components/widgets/catalogList/catalogListItemModel';
import Loader from 'src/components/widgets/loader';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { ZakekeEnvironment, productCatalogDialog_fragment$key } from './__generated__/productCatalogDialog_fragment.graphql';
import { productCatalogDialogQuery } from './__generated__/productCatalogDialogQuery.graphql';

const DialogDecorator = styled(DialogWindow)`
    width: 90%;
    height: 100%;
    padding-top: 20px;
    flex-basis: unset;
`;

const Container = styled.div`
    width: 100%;
    height: 100%;
    margin: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const Title = styled.h3`
    text-align: left;
    margin-bottom: 16px;
    font-size: 18px;
`;

const SearchContainer = styled.div`
    width: 100%;

    input {
        width: calc(100% - 4rem);
    }
`;

const CatalogContainer = styled.div`
    margin-top: 20px;
    flex: 1;
    overflow: auto;
`;

interface CatalogItemModel extends CatalogListItemModel {
    modelID: number;
    zakekeEnvironment: ZakekeEnvironment;
}

interface DialogContentProps {
    search: string;
    scrollContainer?: HTMLDivElement | null;
    onConfirm: (modelID: number) => void;
}

const productCatalogDialogFragment = graphql`
    fragment productCatalogDialog_fragment on Query
    @refetchable(queryName: "productCatalogDialogFragment")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 20 }
        cursor: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: "" }
    ) {
        models(first: $count, after: $cursor, search: $search) @connection(key: "productCatalogDialog_models") {
            edges {
                node {
                    ... on Model {
                        id
                        name
                        modelID
                        code
                        smallImageUrl
                        zakekeEnvironment
                    }
                }
            }
        }
    }
`;

const ProductCatalogDialogContent: FC<DialogContentProps> = ({ search, scrollContainer, onConfirm }) => {
    const query = useLazyLoadQuery<productCatalogDialogQuery>(
        graphql`
            query productCatalogDialogQuery($count: Int!, $cursor: String, $search: String) {
                ...productCatalogDialog_fragment @arguments(count: $count, cursor: $cursor, search: $search)
            }
        `,
        { count: 20, cursor: null, search }
    );

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<productCatalogDialogQuery, productCatalogDialog_fragment$key>(
        productCatalogDialogFragment,
        query
    );

    const [, startTransition] = useTransition();

    const products =
        data.models?.edges?.map((e) => ({
            id: e.node.id,
            modelID: e.node.modelID,
            name: e.node.name,
            code: e.node.code,
            smallImageUrl: e.node.smallImageUrl,
            zakekeEnvironment: e.node.zakekeEnvironment
        })) ?? [];

    // Map to CatalogListItemModel format
    const catalogModels: CatalogItemModel[] = products.map((p) => ({
        id: p.id,
        title: p.name,
        subtitle: p.code,
        thumbnail: p.smallImageUrl || null,
        selected: false,
        modelID: p.modelID,
        zakekeEnvironment: p.zakekeEnvironment
    }));

    useEffect(() => {
        startTransition(() => {
            refetch({ search, count: 20 }, { fetchPolicy: 'network-only' });
        });
    }, [search, refetch]);

    const handleItemClick = (model: CatalogItemModel) => {
        const product = products.find((p) => p.id === model.id);
        if (!product) return;

        onConfirm(product.modelID);
    };

    return (
        <CatalogContainer>
            <CatalogListContent<CatalogItemModel>
                scrollContainer={scrollContainer}
                models={catalogModels}
                loadNext={() => loadNext(20)}
                hasNext={hasNext}
                isLoadingNext={isLoadingNext}
                onItemClick={handleItemClick}
                noItemsMessage={T._('No products found', 'Admin')}
                noItemsSubMessage={search ? T._('Sorry, no product was found for your search. Please try using different keywords.', 'Admin') : undefined}
                hideAlreadyConfiguredBadge={true}
                columns={{
                    xs: 5,
                    md: 7,
                    lg: 7
                }}
            />
        </CatalogContainer>
    );
};

interface ProductCatalogDialogProps {
    onConfirm: (modelID: number) => void;
}

const ProductCatalogDialog: FC<ProductCatalogDialogProps> = ({ onConfirm }) => {
    const [search, setSearch] = useState('');

    return (
        <Dialog windowDecorator={DialogDecorator}>
            <Container>
                <Title>{T._('Select a product from your catalog in Zakeke', 'Admin')}</Title>
                <SearchContainer>
                    <SearchInput placeholder={T._('Search products by name or code...', 'Admin')} onSearch={(criteria) => setSearch(criteria)} />
                </SearchContainer>

                <Suspense fallback={<Loader />}>
                    <ProductCatalogDialogContent search={search} onConfirm={onConfirm} />
                </Suspense>
            </Container>
        </Dialog>
    );
};

export default ProductCatalogDialog;
